import { yupResolver } from '@hookform/resolvers/yup';
import { useQueryClient } from '@tanstack/react-query';
import { format } from 'date-fns';
import { es } from 'date-fns/locale';
import { useRouter } from 'next/router';
import { useTranslation } from 'next-i18next';
import React, { useState } from 'react';
import DatePicker from 'react-datepicker';
import { registerLocale } from 'react-datepicker';
registerLocale('es', es);
import { FormProvider, useForm } from 'react-hook-form';

import {
  useCreateWorkerSettlements,
  useGetSettlementWorkers,
} from '@/modules/Settlements/apiHooks';
import { ISettlements } from '@/modules/Settlements/types';
import { Button, Typography } from '@/shared/components/atoms';
import InfiniteSelectField from '@/shared/components/atoms/SelectField/InfiniteSelectField';
import { validationCreateWorkerSettlementsSchema } from '@/shared/components/molecules/CreateSettlement/logic';
import { getDateFormat } from '@/shared/components/molecules/CustomDatePicker/logic';
import { useStore } from '@/shared/hooks/context/createGenericContext';
import { useModal } from '@/shared/hooks/context/modalContext/useModal';

interface Props {
  startDate: Date | null;
  endDate: Date | null;
}
const CreateWorkerSettlementModal: React.FC<Props> = ({
  startDate,
  endDate,
}) => {
  const {
    t,
    i18n: { language },
  } = useTranslation('common');
  const { push } = useRouter();
  const queryClient = useQueryClient();
  const { closeModal } = useModal();
  const [dateRange, setDateRange] = useState({
    startDate,
    endDate,
  });
  const methods = useForm<ISettlements.ICreateSettlementSchema>({
    resolver: yupResolver(validationCreateWorkerSettlementsSchema(t)),
    mode: 'all',
    criteriaMode: 'firstError',
    shouldFocusError: true,
  });

  const { mutate, isLoading } = useCreateWorkerSettlements();
  const [selectedAsset] = useStore((store) => store?.asset);
  const today = new Date();

  const { data, fetchNextPage } = useGetSettlementWorkers({
    endDate: format(dateRange.endDate || 0, 'yyyy-MM-dd'),
    startDate: format(dateRange.startDate || 0, 'yyyy-MM-dd'),
    entityId: selectedAsset?.id,
    pageSize: 10,
    pageNumber: 1,
  });
  const workersList = data?.pages.flatMap((page) => page.items);

  const submitHandler = (
    value: Partial<ISettlements.ICreateWorkerSettlementSchema>
  ) => {
    if (dateRange.startDate && dateRange.endDate && value.workerId) {
      mutate(
        {
          endDate: format(dateRange.endDate, 'yyyy-MM-dd'),
          startDate: format(dateRange.startDate, 'yyyy-MM-dd'),
          entityId: selectedAsset?.id,
          workerId: value?.workerId,
        },
        {
          onSuccess: (res) => {
            queryClient.invalidateQueries(['settlements']);
            queryClient.invalidateQueries(['getWorkerSettlements']);
            closeModal();
            if (res.data.settlementId) {
              push(`/accounts/settlements/${res.data.settlementId}`);
            }
          },
        }
      );
    }
  };

  return (
    <FormProvider {...methods}>
      <form
        onSubmit={methods.handleSubmit(submitHandler)}
        className='flex h-full flex-col justify-between'
      >
        <div className='mx-auto flex w-full flex-col gap-16 tablet:w-2/3 desktop:w-1/3'>
          <div className='mb-24 mt-11'>
            <Typography
              variant='h2'
              weight='semiBold'
              className='pb-0 capitalize'
            >
              {t('new-settlement')}
            </Typography>

            <Typography
              variant='body1'
              weight='regular'
              className='text-surface-10'
            >
              {t('create-worker-settlement-description')}
            </Typography>
          </div>
          <div className='flex flex-col gap-2'>
            {/* label= */}
            <label className='mb-4'>
              <Typography variant='body1' weight='regular'>
                {t('accounting-period')}
              </Typography>
            </label>
            <div className='flex items-center justify-between gap-16'>
              <div className='flex w-full items-center'>
                {dateRange?.startDate && (
                  <DatePicker
                    className='!w-full'
                    wrapperClassName='relative'
                    locale={language}
                    selected={dateRange.startDate}
                    maxDate={dateRange.endDate}
                    onChange={(date) => {
                      if (date) {
                        setDateRange({ ...dateRange, startDate: date });
                      }
                    }}
                    customInput={
                      <Typography
                        variant='body1'
                        weight='bold'
                        className='w-fit cursor-pointer rounded-lg border-xs border-background-disable bg-white px-11 py-9 text-center'
                      >
                        {getDateFormat(
                          dateRange.startDate,
                          language,
                          'dd/MM/YYY'
                        )}
                      </Typography>
                    }
                  />
                )}
                <span className='text-16 mx-16'>{t('to')}</span>
                {dateRange.endDate && (
                  <DatePicker
                    className='!w-full'
                    wrapperClassName='relative'
                    locale={language}
                    selected={dateRange.endDate}
                    maxDate={today}
                    minDate={dateRange.startDate}
                    onChange={(date) => {
                      if (date) {
                        setDateRange({
                          startDate: dateRange.startDate,
                          endDate: date,
                        });
                      }
                    }}
                    customInput={
                      <Typography
                        variant='body1'
                        weight='bold'
                        className='w-fit cursor-pointer rounded-lg border-xs border-background-disable bg-white px-11 py-9 text-center'
                      >
                        {getDateFormat(
                          dateRange.endDate,
                          language,
                          'dd/MM/YYY'
                        )}
                      </Typography>
                    }
                  />
                )}
              </div>
            </div>
          </div>
          <InfiniteSelectField
            id='workerId'
            isSearchable
            label={t('worker')}
            getLabel={(item: ISettlements.IWorkerInfo) => item?.workerName}
            getValue={(item: ISettlements.IWorkerInfo) => item?.workerId}
            list={workersList || []}
            width='w-full'
            py='0.375rem'
            formatOptionLabel={(option: ISettlements.IWorkerInfo) => (
              <div className='flex flex-col'>
                {!option.isActive && (
                  <span className='text-10 text-surface-10'>
                    {t('inactive')}
                  </span>
                )}
                {option.workerName}
              </div>
            )}
            placeholder={t('worker')}
            fetchNextPage={fetchNextPage}
          />
        </div>
        <div className='flex w-full justify-end border-t-xs border-t-neutralVariant-90 pt-24'>
          <Button
            disabled={Boolean(Object.keys(methods.formState.errors).length)}
            size='none'
            className='!rounded-xl py-2 px-11 '
            type='submit'
            loading={isLoading}
          >
            {t('create-settlement')}
          </Button>
        </div>
      </form>
    </FormProvider>
  );
};
export default CreateWorkerSettlementModal;
